import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Users, TrendingUp, Sparkles, MapPin, Languages, RefreshCw } from "lucide-react";
import {
  ResponsiveContainer,
  LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
  BarChart, Bar,
  PieChart, Pie, Cell, Legend,
} from "recharts";
import { useAuth } from "../lib/auth";
import { adminOverview, adminTimeseries, adminPersonas, adminDestinations, adminLanguage } from "../lib/api";

const COLORS = ["#0A2540", "#4FB3E8", "#E8C874", "#7E57C2", "#26A69A", "#EF5350", "#8D6E63", "#78909C"];

function KpiCard({ label, value, hint, testid }) {
  return (
    <div
      data-testid={testid}
      className="bg-white rounded-2xl border border-[#E8E8ED] p-5 shadow-[0_4px_16px_rgba(0,0,0,0.04)]"
    >
      <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">{label}</div>
      <div className="text-3xl font-semibold text-[#0A2540] tracking-tight">{value}</div>
      {hint && <div className="text-[11px] text-gray-400 mt-1">{hint}</div>}
    </div>
  );
}

function Section({ icon, title, children, testid }) {
  return (
    <section data-testid={testid} className="bg-white rounded-2xl border border-[#E8E8ED] p-5 shadow-[0_4px_16px_rgba(0,0,0,0.04)]">
      <div className="flex items-center gap-2 mb-4">
        {icon}
        <h3 className="text-sm font-semibold text-[#0A2540]">{title}</h3>
      </div>
      {children}
    </section>
  );
}

export default function Dashboard() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [days, setDays] = useState(30);
  const [overview, setOverview] = useState(null);
  const [timeseries, setTimeseries] = useState([]);
  const [personas, setPersonas] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const [langRows, setLangRows] = useState([]);
  const [err, setErr] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  // Route guard: only admins may view. Non-admins & unauth are bounced.
  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/login", { replace: true }); return; }
    if (!user.is_admin) { navigate("/", { replace: true }); return; }
  }, [user, loading, navigate]);

  const load = async () => {
    setRefreshing(true);
    setErr(null);
    try {
      const [o, ts, p, d, l] = await Promise.all([
        adminOverview(days),
        adminTimeseries(days),
        adminPersonas(days),
        adminDestinations(days),
        adminLanguage(days),
      ]);
      setOverview(o);
      setTimeseries(ts.rows || []);
      setPersonas(p.rows || []);
      setDestinations(d.rows || []);
      setLangRows(l.rows || []);
    } catch (e) {
      const status = e.response?.status;
      if (status === 401) navigate("/login", { replace: true });
      else if (status === 403) navigate("/", { replace: true });
      else setErr(e.response?.data?.detail || "Failed to load analytics");
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (user?.is_admin) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.is_admin, days]);

  // Reshape timeseries into wide format for LineChart
  const tsPivot = useMemo(() => {
    const byDay = new Map();
    for (const r of timeseries) {
      const row = byDay.get(r.day) || { day: r.day };
      row[r.event] = (row[r.event] || 0) + r.count;
      byDay.set(r.day, row);
    }
    return Array.from(byDay.values()).sort((a, b) => a.day.localeCompare(b.day));
  }, [timeseries]);

  if (loading || !user?.is_admin) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-500 text-sm">
        Checking access…
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FBFBFD]">
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-white/70 border-b border-[#E8E8ED]">
        <div className="max-w-6xl mx-auto px-4 md:px-6 h-14 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Link to="/" className="p-2 -ml-2 rounded-full hover:bg-[#F5F5F7]" data-testid="dashboard-back">
              <ArrowLeft className="w-5 h-5 text-[#0A2540]" />
            </Link>
            <div className="font-semibold text-[#0A2540]">Analytics · Beta</div>
          </div>
          <div className="flex items-center gap-2">
            <select
              data-testid="dashboard-range"
              value={days}
              onChange={(e) => setDays(parseInt(e.target.value, 10))}
              className="text-xs bg-white border border-[#E8E8ED] rounded-full px-3 py-1.5 outline-none"
            >
              <option value="1">Today</option>
              <option value="7">Last 7 days</option>
              <option value="30">Last 30 days</option>
              <option value="90">Last 90 days</option>
            </select>
            <button
              data-testid="dashboard-refresh"
              onClick={load}
              disabled={refreshing}
              className="p-2 rounded-full hover:bg-[#F5F5F7] disabled:opacity-50"
              title="Refresh"
            >
              <RefreshCw className={`w-4 h-4 text-[#0A2540] ${refreshing ? "animate-spin" : ""}`} />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 md:px-6 py-6 space-y-6">
        {err && (
          <div data-testid="dashboard-error" className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700">
            {err}
          </div>
        )}

        {/* Email sandbox banner — clear signal that Resend is not delivering to real users yet. */}
        {overview?.email?.sandbox && (
          <div
            data-testid="email-sandbox-banner"
            className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-sm text-amber-900 flex items-start gap-3"
          >
            <span aria-hidden="true">⚠️</span>
            <div>
              <div className="font-medium mb-0.5">Email delivery is in SANDBOX mode</div>
              <div className="text-[13px] leading-snug">
                Sender <code className="bg-amber-100 px-1 rounded">{overview.email.sender}</code>{" "}
                only delivers to the API-key owner. Verify a domain at{" "}
                <a className="underline" href="https://resend.com/domains" target="_blank" rel="noreferrer">resend.com/domains</a>{" "}
                and update <code className="bg-amber-100 px-1 rounded">SENDER_EMAIL</code> before public beta.
              </div>
            </div>
          </div>
        )}

        {/* KPIs */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-3">
          <KpiCard testid="kpi-registered" label="Registered" value={overview?.totals?.registered_users ?? "—"} hint="all-time" />
          <KpiCard testid="kpi-returning" label="Returning" value={overview?.totals?.returning_users ?? "—"} hint={`in last ${days}d`} />
          <KpiCard testid="kpi-trips" label="Trips Saved" value={overview?.totals?.trips_saved ?? "—"} hint="all-time" />
          <KpiCard testid="kpi-chats" label="Chat Sessions" value={overview?.totals?.chat_sessions ?? "—"} hint="active only" />
          <KpiCard testid="kpi-premium" label="Premium" value={overview?.totals?.premium_entitlements ?? "—"} hint="active entitlements" />
        </div>

        {/* Funnel counters in-range */}
        <Section testid="section-funnel" icon={<TrendingUp className="w-4 h-4 text-[#4FB3E8]" />} title={`Funnel · last ${days} day${days === 1 ? "" : "s"}`}>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            {Object.entries(overview?.in_range || {}).map(([k, v]) => (
              <div key={k} className="bg-[#F5F5F7] rounded-xl px-3 py-2">
                <div className="text-[10px] uppercase tracking-wide text-gray-500">{k.replace(/_/g, " ")}</div>
                <div className="text-lg font-semibold text-[#0A2540]">{v}</div>
              </div>
            ))}
          </div>
        </Section>

        {/* Timeseries chart */}
        <Section testid="section-timeseries" icon={<Users className="w-4 h-4 text-[#4FB3E8]" />} title="Daily events">
          <div className="h-72 w-full">
            <ResponsiveContainer width="100%" height="100%" minWidth={0}>
              <LineChart data={tsPivot} margin={{ top: 8, right: 16, bottom: 0, left: -8 }}>
                <CartesianGrid stroke="#F0F0F5" strokeDasharray="3 3" />
                <XAxis dataKey="day" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E8E8ED", fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Line type="monotone" dataKey="user_registered"       stroke="#0A2540" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="user_signed_in"        stroke="#4FB3E8" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="conversation_started"  stroke="#7E57C2" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="travel_book_generated" stroke="#26A69A" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="travel_book_pdf_downloaded" stroke="#E8C874" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Section>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Personas */}
          <Section testid="section-personas" icon={<Sparkles className="w-4 h-4 text-[#4FB3E8]" />} title="Top personas">
            {personas.length === 0 ? (
              <div className="text-sm text-gray-400 py-6 text-center">No persona selections yet.</div>
            ) : (
              <div className="h-64 w-full">
                <ResponsiveContainer width="100%" height="100%" minWidth={0}>
                  <PieChart>
                    <Pie data={personas} dataKey="count" nameKey="persona" outerRadius={90} label={{ fontSize: 11 }}>
                      {personas.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E8E8ED", fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </Section>

          {/* Language mix */}
          <Section testid="section-language" icon={<Languages className="w-4 h-4 text-[#4FB3E8]" />} title="Language usage">
            {langRows.length === 0 ? (
              <div className="text-sm text-gray-400 py-6 text-center">No language signal yet.</div>
            ) : (
              <div className="h-64 w-full">
                <ResponsiveContainer width="100%" height="100%" minWidth={0}>
                  <BarChart data={langRows} margin={{ top: 8, right: 8, bottom: 8, left: -16 }}>
                    <CartesianGrid stroke="#F0F0F5" strokeDasharray="3 3" />
                    <XAxis dataKey="lang" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E8E8ED", fontSize: 12 }} />
                    <Bar dataKey="count" fill="#4FB3E8" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </Section>
        </div>

        {/* Destinations table */}
        <Section testid="section-destinations" icon={<MapPin className="w-4 h-4 text-[#4FB3E8]" />} title="Top destinations">
          {destinations.length === 0 ? (
            <div className="text-sm text-gray-400 py-6 text-center">No Travel Books generated yet in this range.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wide text-gray-500 border-b border-[#F0F0F5]">
                    <th className="py-2">Destination</th>
                    <th className="py-2 text-right">Travel Books</th>
                  </tr>
                </thead>
                <tbody>
                  {destinations.map((r, i) => (
                    <tr key={i} className="border-b border-[#F5F5F7] last:border-0">
                      <td className="py-2 text-[#1D1D1F]">{r.destination}</td>
                      <td className="py-2 text-right font-semibold text-[#0A2540]">{r.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Section>

        <div className="text-[11px] text-gray-400 text-center py-4">
          Private dashboard · admin-only · window: last {days} day{days === 1 ? "" : "s"}
        </div>
      </main>
    </div>
  );
}
